import { PALETTE } from '../tokens';
import { DOMAIN_META } from '../utils/domainLabels';
import DomainInfoButton from './DomainInfoButton';

const CALIBRATING_DOT_KEYFRAMES = `
  @keyframes domainSubScoresCalibratingPulse {
    0%, 100% { opacity: 0.35; }
    50%      { opacity: 1; }
  }
`;

/**
 * Convert a 0.0–1.0 domain score to a 1–5 dot count.
 *   0.0–0.19 → 1 | 0.2–0.39 → 2 | 0.4–0.59 → 3 | 0.6–0.79 → 4 | 0.8–1.0 → 5
 */
export function scoreToDots(score) {
  if (score == null || score < 0) return 1;
  if (score < 0.2) return 1;
  if (score < 0.4) return 2;
  if (score < 0.6) return 3;
  if (score < 0.8) return 4;
  return 5;
}

/**
 * DomainDots — the five-dot indicator on its own.
 *
 * Extracted so Profile's domain summary can speak the same visual
 * language without inheriting this file's card chrome or its dependency
 * on plain-language labels from the insights API (Profile has neither).
 * One implementation of "what does a score look like" across both
 * surfaces; a second copy would drift the thresholds.
 *
 *   score  — 0.0–1.0 float
 *   muted  — render every dot neutral + pulsing (calibrating state)
 */
export function DomainDots({ score, muted = false, gap = 5, size = 8 }) {
  const dotCount = scoreToDots(score);
  return (
    <div style={{ display: 'flex', gap, flex: '0 0 auto' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <div
          key={n}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: muted
              ? PALETTE.border
              : (n <= dotCount ? PALETTE.primary : PALETTE.border),
            transition: 'background 0.3s ease',
            animation: muted
              ? `domainSubScoresCalibratingPulse 1.8s ease-in-out ${n * 0.14}s infinite`
              : 'none',
          }}
        />
      ))}
    </div>
  );
}

/**
 * DomainSubScores — three rows (Body / Mind / Behavior) with dot indicators.
 *
 * Props:
 *   bioScore   — 0.0–1.0 float
 *   psychScore — 0.0–1.0 float
 *   behScore   — 0.0–1.0 float
 *   bioLabel   — plain-language label from insights API
 *   psychLabel — plain-language label from insights API
 *   behLabel   — plain-language label from insights API
 *   loading    — show skeleton
 *   error      — API error
 */
export function DomainSubScores({
  bioScore,
  psychScore,
  behScore,
  bioLabel,
  psychLabel,
  behLabel,
  coherenceStatus,
  loading = false,
  error = false,
}) {
  if (loading) return <DomainSubScoresSkeleton />;
  if (error) return null;

  const scores = { bio: bioScore, psych: psychScore, beh: behScore };
  const labels = { bio_label: bioLabel, psych_label: psychLabel, beh_label: behLabel };
  // BEH is the most data-sparse domain early on. While calibrating, a
  // declining absolute number reads as personal failure rather than
  // algorithmic baseline-building. Surface directional copy instead.
  const isCalibrating = coherenceStatus === 'CALIBRATING';

  return (
    <>
    <style>{CALIBRATING_DOT_KEYFRAMES}</style>
    <div
      style={{
        background: PALETTE.surface,
        borderRadius: 20,
        padding: '18px 22px',
        boxShadow: '0 1px 16px rgba(28,24,20,0.07)',
      }}
    >
      {DOMAIN_META.map((domain, idx) => {
        const gateBeh = domain.key === 'beh' && isCalibrating;
        const label = gateBeh
          ? 'Behavior patterns calibrating'
          : (labels[domain.labelField] || '—');
        return (
          <div key={domain.key}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 0',
              }}
            >
              {/* Domain name + per-domain "why this score" explainer */}
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 13,
                  fontWeight: 500,
                  color: PALETTE.text,
                  minWidth: 72,
                  flex: '0 0 auto',
                }}
              >
                {domain.display}
                <DomainInfoButton domain={domain.key} />
              </span>

              {/* Dots — gated to a neutral animated row while calibrating BEH */}
              <div style={{ margin: '0 16px' }}>
                <DomainDots score={scores[domain.key]} muted={gateBeh} />
              </div>

              {/* Plain-language label */}
              <span
                style={{
                  fontSize: 12,
                  color: PALETTE.textMid,
                  fontStyle: gateBeh ? 'italic' : 'normal',
                  fontWeight: 300,
                  textAlign: 'right',
                  flex: 1,
                  minWidth: 0,
                  // Wrap rather than truncate — a clipped label tells the
                  // user nothing. Labels are short (≤22 chars), so this
                  // stays within two lines.
                  lineHeight: 1.28,
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                }}
              >
                {label}
              </span>
            </div>
            {idx < DOMAIN_META.length - 1 && (
              <div style={{ height: 1, background: PALETTE.surfaceAlt }} />
            )}
          </div>
        );
      })}
    </div>
    </>
  );
}

function DomainSubScoresSkeleton() {
  return (
    <div
      style={{
        background: PALETTE.surface,
        borderRadius: 20,
        padding: '18px 22px',
        boxShadow: '0 1px 16px rgba(28,24,20,0.07)',
      }}
    >
      {[0, 1, 2].map((i) => (
        <div key={i}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 0',
            }}
          >
            <div
              style={{
                width: 56,
                height: 13,
                background: PALETTE.surfaceAlt,
                borderRadius: 6,
              }}
            />
            <div style={{ display: 'flex', gap: 5 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <div
                  key={n}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: PALETTE.surfaceAlt,
                  }}
                />
              ))}
            </div>
            <div
              style={{
                width: 80,
                height: 12,
                background: PALETTE.surfaceAlt,
                borderRadius: 6,
              }}
            />
          </div>
          {i < 2 && <div style={{ height: 1, background: PALETTE.surfaceAlt }} />}
        </div>
      ))}
    </div>
  );
}
